import { Button, Group, Stack } from '@mantine/core';
import { FormattedMessage } from 'react-intl';

import { AccessorialsSection } from './AccessorialsSection';
import { freightFormMessages } from './freight-form-messages';
import { HandlingUnitCard } from './HandlingUnitCard';
import { InsuranceSection } from './InsuranceSection';
import { ShipFromSection } from './ShipFromSection';
import { ShipToSection } from './ShipToSection';
import { applyLtlPickupServicesToAdditionalServices } from '../../../../ltl/ltl-defaults';

import type { DimensionUnit } from '../../../../types/dimensions';
import type { LtlAdditionalServices, LtlHandlingUnit } from '../../../../types/ltl';
import type { WeightUnit } from '../../../../types/weight';
import type { FreightDensityAction } from '../../../freight-density/RecommendedFreightClassModal';
import type { FreightBrowserDestination, FreightBrowserFormState, ShipFromOption } from '../../freight-browser-types';

type FreightFormProps = {
  formState: FreightBrowserFormState;
  onFormChange: (next: FreightBrowserFormState) => void;
  shipFromOptions: ShipFromOption[];
  onAddShipFrom?: () => void;
  isHazmatEnabled?: boolean;
  onDensityAction?: (action: FreightDensityAction) => void;
  sellerFreightProviderId?: number;
  displayWeightUnit?: WeightUnit;
  displayDimensionUnit?: DimensionUnit;
  zIndex?: number;
};

const createEmptyHandlingUnit = (existingUnits: LtlHandlingUnit[]): LtlHandlingUnit => {
  const referenceUnit = existingUnits[0];
  const dimensionUnit = referenceUnit?.dimensionUnit;
  const weightUnit = referenceUnit?.commodities[0]?.weightUnit;

  return {
    id: crypto.randomUUID(),
    packagingType: 'Pallet',
    quantity: 1,
    length: 0,
    width: 0,
    height: 0,
    dimensionUnit,
    isStackable: true,
    commodities: [
      {
        id: crypto.randomUUID(),
        description: '',
        freightClass: '',
        nmfcCode: '',
        quantity: 1,
        weight: 0,
        weightUnit,
        isHazmat: false,
        hazmatDetails: null,
        hazmatInfo: null,
        insurance: { isInsured: false, commodityCategoryCode: '', condition: '', declaredValue: 0 },
      },
    ],
  };
};

export const FreightForm = ({
  formState,
  onFormChange,
  shipFromOptions,
  onAddShipFrom,
  isHazmatEnabled,
  onDensityAction,
  sellerFreightProviderId,
  displayWeightUnit,
  displayDimensionUnit,
  zIndex,
}: FreightFormProps) => {
  const handleShipFromChange = (shipFromId: string) => {
    const nextOption = shipFromOptions.find((option) => option.id === shipFromId);
    onFormChange({
      ...formState,
      shipFromId,
      additionalServices: applyLtlPickupServicesToAdditionalServices(formState.additionalServices, nextOption?.ltlDefaults?.pickupServices),
    });
  };

  const handleDestinationChange = (destination: FreightBrowserDestination) => {
    onFormChange({ ...formState, destination });
  };

  const handleUnitChange = (index: number, updated: LtlHandlingUnit) => {
    const handlingUnits = [...formState.handlingUnits];
    handlingUnits[index] = updated;
    onFormChange({ ...formState, handlingUnits });
  };

  const handleAddUnit = () => {
    onFormChange({ ...formState, handlingUnits: [...formState.handlingUnits, createEmptyHandlingUnit(formState.handlingUnits)] });
  };

  const handleRemoveUnit = (index: number) => {
    onFormChange({ ...formState, handlingUnits: formState.handlingUnits.filter((_, idx) => idx !== index) });
  };

  const handleAccessorialsChange = (additionalServices: LtlAdditionalServices) => {
    onFormChange({ ...formState, additionalServices });
  };

  const handleInsuranceChange = (insurance: FreightBrowserFormState['insurance']) => {
    onFormChange({ ...formState, insurance });
  };

  return (
    <Stack gap='md'>
      <Group grow align='flex-start'>
        <ShipFromSection
          onAddShipFrom={onAddShipFrom}
          onChange={handleShipFromChange}
          options={shipFromOptions}
          value={formState.shipFromId}
        />
        <ShipToSection onChange={handleDestinationChange} value={formState.destination} />
      </Group>
      {formState.handlingUnits.map((unit, idx) => (
        <HandlingUnitCard
          displayDimensionUnit={displayDimensionUnit}
          displayWeightUnit={displayWeightUnit}
          index={idx}
          isHazmatEnabled={isHazmatEnabled}
          key={unit.id}
          onChange={(updated) => handleUnitChange(idx, updated)}
          onDensityAction={onDensityAction}
          onRemove={idx > 0 ? () => handleRemoveUnit(idx) : undefined}
          sellerFreightProviderId={sellerFreightProviderId}
          unit={unit}
          zIndex={zIndex}
        />
      ))}
      <Button data-testid='add-unit-button' onClick={handleAddUnit} variant='tertiary'>
        <FormattedMessage {...freightFormMessages.addUnit} />
      </Button>
      <AccessorialsSection onChange={handleAccessorialsChange} value={formState.additionalServices} />
      <InsuranceSection onChange={handleInsuranceChange} value={formState.insurance} />
    </Stack>
  );
};
